import React, { useMemo, useState } from "react";
import {
  Box,
  ButtonBase,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import "dayjs/locale/es";
import { useTranslation } from "react-i18next";

function ChipField({ label, valueLabel, onClick, active, icon, flex = "1 1 0" }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  // Never alpha(action.hover, …): action.hover is already rgba; alpha() rebuilds as solid black.
  const idleBg = isDark
    ? alpha(theme.palette.common.white, 0.04)
    : theme.palette.background.paper;
  const idleHoverBg = alpha(theme.palette.text.primary, isDark ? 0.08 : 0.04);
  const activeBg = alpha(theme.palette.primary.main, isDark ? 0.2 : 0.1);
  const activeHoverBg = alpha(theme.palette.primary.main, isDark ? 0.28 : 0.14);

  return (
    <ButtonBase
      onClick={onClick}
      disableRipple
      sx={{
        px: 1.35,
        py: 1,
        borderRadius: 2,
        textAlign: "left",
        flex,
        minWidth: 0,
        justifyContent: "flex-start",
        alignItems: "stretch",
        bgcolor: active ? activeBg : idleBg,
        border: 1,
        borderColor: active ? "primary.main" : "divider",
        boxShadow: active ? `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.28)}` : "none",
        color: "text.primary",
        transition: theme.transitions.create(["background-color", "border-color", "box-shadow"], {
          duration: theme.transitions.duration.shorter,
        }),
        "&:hover": {
          bgcolor: active ? activeHoverBg : idleHoverBg,
          borderColor: active
            ? "primary.main"
            : alpha(theme.palette.text.primary, isDark ? 0.32 : 0.2),
        },
        "&.Mui-focusVisible": {
          outline: `2px solid ${alpha(theme.palette.primary.main, 0.5)}`,
          outlineOffset: 1,
        },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, width: "100%" }}>
        {icon ? (
          <Box
            sx={{
              color: active ? "primary.main" : "text.secondary",
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              "& svg": { fontSize: 18 },
            }}
          >
            {icon}
          </Box>
        ) : null}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="caption"
            color={active ? "primary.main" : "text.secondary"}
            fontWeight={700}
            display="block"
            sx={{ lineHeight: 1.15, mb: 0.2, letterSpacing: 0.01 }}
          >
            {label}
          </Typography>
          <Typography
            variant="body2"
            fontWeight={700}
            noWrap
            sx={{ color: "text.primary", lineHeight: 1.25 }}
          >
            {valueLabel}
          </Typography>
        </Box>
      </Stack>
    </ButtonBase>
  );
}

function buildTimeOptions(stepMinutes = 15) {
  const options = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += stepMinutes) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    options.push({
      minutes,
      label: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`,
    });
  }
  return options;
}

const TIME_OPTIONS = buildTimeOptions(15);

/**
 * Google-like start/end chips: separate date and time (or two dates when all-day).
 * Keeps duration when start moves. Theme-aware outlined chips.
 */
export default function AgendaDateTimeFields({ start, end, allDay, onChange }) {
  const { t, i18n } = useTranslation("teamAgenda");
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [anchor, setAnchor] = useState(null);
  const [mode, setMode] = useState(null);

  const startAt = useMemo(() => {
    const d = dayjs(String(start || "").replace(" ", "T"));
    return d.isValid() ? d : dayjs().minute(0).second(0);
  }, [start]);
  const endAt = useMemo(() => {
    const d = dayjs(String(end || start || "").replace(" ", "T"));
    return d.isValid() ? d : startAt.add(1, "hour");
  }, [end, start, startAt]);

  const durationMs = Math.max(endAt.diff(startAt), allDay ? 86400000 : 15 * 60000);
  const locale = i18n.language?.startsWith("pt")
    ? "pt-br"
    : i18n.language?.startsWith("es")
      ? "es"
      : "en";

  const openPicker = (event, nextMode) => {
    setAnchor(event.currentTarget);
    setMode(nextMode);
  };
  const closePicker = () => {
    setAnchor(null);
    setMode(null);
  };

  const emit = (nextStart, nextEnd) => {
    onChange?.({
      start: allDay ? nextStart.format("YYYY-MM-DD") : nextStart.format("YYYY-MM-DDTHH:mm"),
      end: allDay ? nextEnd.format("YYYY-MM-DD") : nextEnd.format("YYYY-MM-DDTHH:mm"),
    });
  };

  const applyStartDate = (value) => {
    if (!value?.isValid?.()) return;
    const nextStart = startAt.year(value.year()).month(value.month()).date(value.date());
    let nextEnd = dayjs(nextStart.valueOf() + durationMs);
    if (!nextEnd.isAfter(nextStart)) nextEnd = nextStart.add(allDay ? 1 : 1, allDay ? "day" : "hour");
    emit(nextStart, nextEnd);
    closePicker();
  };

  const applyEndDate = (value) => {
    if (!value?.isValid?.()) return;
    let nextEnd = endAt.year(value.year()).month(value.month()).date(value.date());
    if (!allDay) nextEnd = nextEnd.hour(endAt.hour()).minute(endAt.minute());
    if (!nextEnd.isAfter(startAt)) {
      nextEnd = allDay ? startAt.add(1, "day") : startAt.add(durationMs, "millisecond");
    }
    emit(startAt, nextEnd);
    closePicker();
  };

  const applyStartTime = (label) => {
    const [h, m] = label.split(":").map(Number);
    const nextStart = startAt.hour(h).minute(m).second(0);
    const nextEnd = dayjs(nextStart.valueOf() + durationMs);
    emit(nextStart, nextEnd.isAfter(nextStart) ? nextEnd : nextStart.add(1, "hour"));
    closePicker();
  };

  const applyEndTime = (label) => {
    const [h, m] = label.split(":").map(Number);
    let nextEnd = endAt.hour(h).minute(m).second(0);
    if (!nextEnd.isAfter(startAt)) nextEnd = startAt.add(15, "minute");
    emit(startAt, nextEnd);
    closePicker();
  };

  const dateLabel = (value) =>
    value.format(value.isSame(dayjs(), "year") ? "ddd, D MMM" : "ddd, D MMM YYYY");
  const timeLabel = (value) => value.format("HH:mm");
  const selectedTime =
    mode === "startTime" ? timeLabel(startAt) : mode === "endTime" ? timeLabel(endAt) : "";
  const spansDays = !allDay && !endAt.isSame(startAt, "day");

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
      <Stack spacing={1}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <ChipField
            label={t("fieldDate", "Data")}
            valueLabel={dateLabel(startAt)}
            active={mode === "startDate"}
            onClick={(e) => openPicker(e, "startDate")}
            icon={<CalendarMonthOutlinedIcon />}
            flex={{ xs: "1 1 auto", sm: "1.35 1 0" }}
          />
          {!allDay ? (
            <>
              <ChipField
                label={t("fieldStart")}
                valueLabel={timeLabel(startAt)}
                active={mode === "startTime"}
                onClick={(e) => openPicker(e, "startTime")}
                icon={<ScheduleOutlinedIcon />}
              />
              <Typography
                component="span"
                aria-hidden
                sx={{
                  display: { xs: "none", sm: "inline" },
                  color: "text.disabled",
                  fontWeight: 700,
                  px: 0.25,
                  userSelect: "none",
                }}
              >
                –
              </Typography>
              <ChipField
                label={t("fieldEnd")}
                valueLabel={spansDays ? `${dateLabel(endAt)} · ${timeLabel(endAt)}` : timeLabel(endAt)}
                active={mode === "endTime"}
                onClick={(e) => openPicker(e, "endTime")}
                icon={<ScheduleOutlinedIcon />}
                flex={spansDays ? { xs: "1 1 auto", sm: "1.35 1 0" } : "1 1 0"}
              />
            </>
          ) : (
            <ChipField
              label={t("fieldEnd")}
              valueLabel={dateLabel(endAt)}
              active={mode === "endDate"}
              onClick={(e) => openPicker(e, "endDate")}
              icon={<CalendarMonthOutlinedIcon />}
            />
          )}
        </Stack>
        {spansDays ? (
          <Typography variant="caption" color="text.secondary" sx={{ pl: 0.25 }}>
            {t("dateTimeSpansDays", "O horário de término fica em outro dia.")}
          </Typography>
        ) : null}
      </Stack>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={closePicker}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            elevation: 8,
            sx: {
              borderRadius: 2.5,
              overflow: "hidden",
              mt: 0.75,
              border: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
              backgroundImage: "none",
            },
          },
        }}
      >
        {mode === "startDate" || mode === "endDate" ? (
          <Box
            sx={{
              "& .MuiPickersLayout-root": {
                bgcolor: "background.paper",
              },
              "& .MuiPickersDay-root.Mui-selected": {
                bgcolor: "primary.main",
                color: "primary.contrastText",
              },
            }}
          >
            <StaticDatePicker
              displayStaticWrapperAs="desktop"
              value={mode === "startDate" ? startAt : endAt}
              onChange={(value) => (mode === "startDate" ? applyStartDate(value) : applyEndDate(value))}
              slotProps={{ actionBar: { actions: [] } }}
            />
          </Box>
        ) : null}
        {mode === "startTime" || mode === "endTime" ? (
          <List
            dense
            disablePadding
            sx={{
              width: 148,
              maxHeight: 288,
              overflowY: "auto",
              py: 0.75,
              bgcolor: "background.paper",
            }}
            ref={(node) => {
              if (!node || !selectedTime) return;
              const el = node.querySelector(`[data-time="${selectedTime}"]`);
              if (el) el.scrollIntoView({ block: "center" });
            }}
          >
            {TIME_OPTIONS.map((opt) => {
              const selected = opt.label === selectedTime;
              return (
                <ListItemButton
                  key={opt.label}
                  data-time={opt.label}
                  selected={selected}
                  onClick={() =>
                    mode === "startTime" ? applyStartTime(opt.label) : applyEndTime(opt.label)
                  }
                  sx={{
                    mx: 0.75,
                    my: 0.15,
                    borderRadius: 1.5,
                    py: 0.65,
                    "&.Mui-selected": {
                      bgcolor: alpha(theme.palette.primary.main, isDark ? 0.22 : 0.12),
                      color: "primary.main",
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, isDark ? 0.28 : 0.16),
                      },
                    },
                  }}
                >
                  <ListItemText
                    primary={opt.label}
                    primaryTypographyProps={{
                      fontWeight: selected ? 800 : 500,
                      textAlign: "center",
                      variant: "body2",
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        ) : null}
      </Popover>
    </LocalizationProvider>
  );
}
